import { ConditionsHandler, DetailsHandler, RESOURCES } from "@/organisms/Form"
import {
  getPolicyCategory,
  getPolicySubType
} from "@/pages/GovernancePage/applications/policies/usePolicies"
import { getPoliciesScm } from "@/pages/GovernancePage/applications/policies/usePolicies.selector"
import { canAllowPolicyExceptions } from "@/pages/GovernancePage/helpers/policy"
import { ScopeSpecificResourcesHandler } from "./scope-specific-resources.payload"

import type {
  Action,
  PolicyCategory,
  PolicyType,
  SubType
} from "@/api/usePolicies.types"
import type { TFunction } from "@/hooks/useTranslation"

type Values = ReturnType<typeof getInitialValues>

export const getInitialValues = (
  response: ReturnType<typeof getPoliciesScm> | undefined,
  isNewEntity: boolean
) => {
  const defaultValues = {
    resources: RESOURCES.ALL,
    [RESOURCES.SPECIFIC]: [{}],
    conditions: [[], []] as Record<string, unknown>[][],
    allowPolicyExceptions: false
  }

  if (isNewEntity) return defaultValues

  const {
    name,
    description,
    action,
    currentConditions,
    specificScopes,
    allowPolicyExceptions
  } = response?.data?.[0] || {}
  const isSpecificScope = !!specificScopes?.length

  return {
    action,
    resources: isSpecificScope ? RESOURCES.SPECIFIC : RESOURCES.ALL,
    [RESOURCES.SPECIFIC]: isSpecificScope
      ? specificScopes
      : defaultValues[RESOURCES.SPECIFIC],
    conditions: currentConditions,
    name_details: name,
    description_details: description,
    allowPolicyExceptions: !!allowPolicyExceptions
  }
}

export const getRules = (t: TFunction) => [
  { required: true, message: t("error.requiredField") },
  {
    validator(_: unknown, value: unknown) {
      if (!Array.isArray(value) || value?.findIndex((v) => v === "*") === -1) {
        return Promise.resolve()
      }

      return Promise.reject(new Error("Invalid value"))
    }
  }
]

export const getPayload = (
  values: Values & { action: Action; enabled: boolean },
  params: {
    type: PolicyType
    subType: SubType
    enabled?: boolean
    policyCategory?: PolicyCategory
  }
) => {
  const { type, subType, policyCategory } = params
  const { action, enabled } = values
  const details = new DetailsHandler()

  details
    .setNext(new ConditionsHandler())
    .setNext(new ScopeSpecificResourcesHandler())

  const { name, description, conditions, scope } = details.handle(values)

  // For CI policies, use the provided policyCategory; for SCM, derive from type
  const resolvedCategory = policyCategory || getPolicyCategory(type)
  const isAllowed = canAllowPolicyExceptions({ type, subType, action })
  // Left out when the field is hidden, so a save keeps the stored setting.
  const policyExceptions = isAllowed
    ? { allowPolicyExceptions: !!values.allowPolicyExceptions }
    : {}

  return {
    policy: {
      name,
      description,
      conditions,
      scope,
      action,
      enabled,
      policyType: getPolicySubType(subType),
      policyCategory: resolvedCategory,
      ...policyExceptions
    }
  }
}
